import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { formatPhoneNumber } from "@/utils/formatUtils";
import { Button, Divider } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserGetUserTotpDevices } from "../../../../../common/api/queries";
import ActionTextIcon from "../../../../../common/components/icons/ActionTextIcon";
import AntIcon from "../../../../../common/components/icons/AntIcon";
import { AgentUserProfile, ClientUserProfile, UserTotpDevice } from "../../../types";
import { CreateUserTotpDeviceForm } from "../../forms/CreateUserTotpDeviceForm";
import { DeleteUserTotpDeviceForm } from "../../forms/DeleteUserTotpDeviceForm";
import { PhoneUpdateForm } from "../../forms/PhoneUpdateForm";
import { RequestEmailUpdateForm } from "../../forms/RequestEmailUpdateForm";
import { UpdatePasswordForm } from "../../forms/UpdatePasswordForm";
import { UpdateUserTotpDeviceForm } from "../../forms/UpdateUserTotpDeviceForm";
import UserTotpDeviceTableView from "./UserTotpDeviceTableView";

interface Props {
  user: AgentUserProfile | ClientUserProfile;
  openTotpDeviceFormByDefault: boolean;
}

type OpenForm =
  | { type: "email-update" }
  | { type: "phone-update" }
  | { type: "password-update" }
  | { type: "totp-device-create" }
  | { type: "totp-device-update"; totpDevice: UserTotpDevice }
  | { type: "totp-device-delete"; totpDevice: UserTotpDevice };

export const UserProfileUserView = ({ user, openTotpDeviceFormByDefault }: Props) => {
  const { t } = useTranslation();

  const { data: totpDevices } = useUserGetUserTotpDevices(user.id);

  const [openForm, setOpenForm] = useState<OpenForm | undefined>(
    openTotpDeviceFormByDefault ? { type: "totp-device-create" } : undefined,
  );

  const closeForm = () => setOpenForm(undefined);

  return (
    <>
      <h2>
        {t("user.titles.userData")} | {user.name}
      </h2>

      <ItemCreatedUpdatedInfoView item={user} />

      <Divider titlePlacement="start" className="divider-subheader">
        {t("user.titles.basicData")}
      </Divider>

      <table className="data-table-view">
        <tbody>
          <tr>
            <td>{t("user.attrs.name")}:</td>
            <td>{user.name}</td>
          </tr>

          <tr>
            <td>{t("user.attrs.email")}:</td>
            <td>{user.email}</td>
            <td>
              <ActionTextIcon
                icon="edit"
                color="blue"
                text={t("user.actions.updateEmail")}
                onClick={() => setOpenForm({ type: "email-update" })}
              />
            </td>
          </tr>
          <tr>
            <td>{t("user.attrs.phone")}:</td>
            <td>{user.phone ? formatPhoneNumber(user.phone) : ""}</td>
            <td>
              <ActionTextIcon
                icon="edit"
                color="blue"
                text={t("user.actions.updatePhone")}
                onClick={() => setOpenForm({ type: "phone-update" })}
              />
            </td>
          </tr>
        </tbody>
      </table>

      <Button
        icon={<AntIcon type="key" />}
        type="primary"
        className="margin-top-tiny margin-bottom-small"
        onClick={() => setOpenForm({ type: "password-update" })}
      >
        {t("user.actions.changePassword")}
      </Button>

      <Divider titlePlacement="start" className="divider-subheader">
        {t("user.totpDevice.titles.list")}
      </Divider>

      <UserTotpDeviceTableView
        totpDevices={totpDevices ?? []}
        onCreateClick={() => setOpenForm({ type: "totp-device-create" })}
        onUpdateClick={(totpDevice) => setOpenForm({ type: "totp-device-update", totpDevice })}
        onDeleteClick={(totpDevice) => setOpenForm({ type: "totp-device-delete", totpDevice })}
      />

      <RequestEmailUpdateForm open={openForm?.type === "email-update"} user={user} onFormCancel={closeForm} />

      <PhoneUpdateForm open={openForm?.type === "phone-update"} user={user} onFormCancel={closeForm} />

      <UpdatePasswordForm open={openForm?.type === "password-update"} user={user} onFormCancel={closeForm} />

      <CreateUserTotpDeviceForm open={openForm?.type === "totp-device-create"} user={user} onFormCancel={closeForm} />

      <UpdateUserTotpDeviceForm
        user={user}
        totpDevice={openForm?.type === "totp-device-update" ? openForm.totpDevice : undefined}
        onFormCancel={closeForm}
      />

      <DeleteUserTotpDeviceForm
        user={user}
        totpDevice={openForm?.type === "totp-device-delete" ? openForm.totpDevice : undefined}
        onFormCancel={closeForm}
      />
    </>
  );
};
